import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  approvePersonalShadowAiTools,
  approveShadowAiTools,
  getPersonalShadowAiToolApprovalCounts,
  getShadowAiToolApprovalCounts,
  unapprovePersonalShadowAiTools,
  unapproveShadowAiTools,
} from '@kirin/api-client/api/shadow-ai';
import type {
  ToolApprovalCounts,
  ToolApprovalMutationResponse,
  ToolApprovalScope,
  ToolApprovalTab,
} from '@kirin/api-client/contracts/shadow-ai-tool-approvals';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from './errorHelpers';
import { queryKeys } from './queryKeys';

export type ToolApprovalAction = 'approve' | 'unapprove';

export interface ToolApprovalMutationVariables {
  action: ToolApprovalAction;
  scope: ToolApprovalScope;
  toolIds: string[];
}

export interface UseToolApprovalCountsOptions {
  /** False while the feature flag is off / loading — no request is sent. */
  enabled: boolean;
  /** KIR-3495 user-scope drill-down: totals for one member's rows. */
  scopeUserId?: string;
  /** Match the table's own polling so counts and rows stay in step. */
  refetchInterval?: number;
}

/**
 * KIR-4854: Show-filter totals for one Shadow AI Tools tab. Tab totals by
 * design — independent of the table's search and column filters.
 */
export function useToolApprovalCounts(
  organizationId: string,
  tab: ToolApprovalTab,
  { enabled, scopeUserId, refetchInterval }: UseToolApprovalCountsOptions
): UseQueryResult<ToolApprovalCounts> {
  return useQuery({
    queryKey: queryKeys.shadowAi.toolApprovalCounts(
      organizationId,
      tab,
      scopeUserId
    ),
    queryFn: ({ signal }) =>
      getShadowAiToolApprovalCounts(
        apiClient,
        organizationId,
        { tab, ...(scopeUserId ? { scopeUserId } : {}) },
        { signal }
      ),
    enabled: enabled && !!organizationId,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    refetchInterval,
  });
}

export interface UsePersonalToolApprovalCountsOptions {
  /** False while the feature flag is off / loading — no request is sent. */
  enabled: boolean;
  /** Match the table's own polling so counts and rows stay in step. */
  refetchInterval?: number;
}

/**
 * KIR-4864: Show-filter totals for one personal Shadow AI tab — the user's own
 * rows against their personal approvals. Tab totals, like the org ones.
 */
export function usePersonalToolApprovalCounts(
  tab: ToolApprovalTab,
  { enabled, refetchInterval }: UsePersonalToolApprovalCountsOptions
): UseQueryResult<ToolApprovalCounts> {
  // Key on identity so a switched account never reads the prior user's cache,
  // and wait for that identity: a request sent before it loads would be cached
  // under no user, where the next account could read it.
  const { user } = useAuth();
  return useQuery({
    queryKey: queryKeys.shadowAi.personalToolApprovalCounts(tab, user?.email),
    queryFn: ({ signal }) =>
      getPersonalShadowAiToolApprovalCounts(apiClient, { tab }, { signal }),
    enabled: enabled && !!user?.email,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    refetchInterval,
  });
}

/**
 * KIR-4854: approve / unapprove tools org-wide; `organizationId` null =
 * the caller's personal account (KIR-4864), which never touches an org's
 * approvals. On success every Shadow AI
 * table, the Running Now tables (separate `runningNow` root — `shadowAi.all`
 * does not reach them) and the Show-filter counts refetch, so approving a tool
 * in Installed AI Tools is reflected in Running Now too (shared `endpoint`
 * scope). Errors surface as a destructive toast.
 */
export function useToolApprovalMutation(
  organizationId: string | null
): UseMutationResult<
  ToolApprovalMutationResponse,
  unknown,
  ToolApprovalMutationVariables
> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ action, scope, toolIds }: ToolApprovalMutationVariables) => {
      const body = { scope, toolIds };
      if (organizationId === null) {
        return action === 'approve'
          ? approvePersonalShadowAiTools(apiClient, body)
          : unapprovePersonalShadowAiTools(apiClient, body);
      }
      return action === 'approve'
        ? approveShadowAiTools(apiClient, organizationId, body)
        : unapproveShadowAiTools(apiClient, organizationId, body);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.shadowAi.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.runningNow.all }),
      ]);
    },
    onError: (error, variables) => {
      const noun = variables.toolIds.length > 1 ? 'tools' : 'tool';
      toast({
        variant: 'destructive',
        title:
          variables.action === 'approve'
            ? `Failed to approve ${noun}`
            : `Failed to unapprove ${noun}`,
        description: getErrorMessage(error),
      });
    },
  });
}
